import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  Output,
  PLATFORM_ID,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { ASSET_CONSTANTS } from '../../../shared/constants/asset-constants';
import { TruncatePipe } from '../../../shared/pipes/truncate.pipe';

@Component({
  selector: 'app-mb-select',
  imports: [
    NzSelectModule,
    ReactiveFormsModule,
    FormsModule,
    NzIconModule,
    CommonModule,
    TruncatePipe,
    TranslocoDirective,
  ],
  templateUrl: './mb-select.component.html',
  styleUrls: ['./mb-select.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MbSelectComponent),
      multi: true,
    },
  ],
})
export class MbSelectComponent implements ControlValueAccessor {
  @Input() placeholder = '';
  @Input() prefixLabel = '';
  @Input() options: { value: string; label: string }[] = [];
  @Input() containerClass = '';
  @Input() errorMessage = '';
  @Input() hasError = false;
  @Input() hasTranslation = false;
  @Input() hasShortText = false;
  @Input() isFullyDisplayed = false;
  @Input() searchable = true;
  @Input() compactMobileDropdown = false;
  @Output() handleChange = new EventEmitter<string>();
  @Input() set disabled(value: boolean) {
    this.inputDisabled = !!value;
  }
  get disabled(): boolean {
    return this.inputDisabled || this.controlDisabled;
  }
  value = '';
  protected readonly ASSET_CONSTANTS = ASSET_CONSTANTS;
  private readonly platformId = inject(PLATFORM_ID);
  private inputDisabled = false;
  private controlDisabled = false;

  private onChange: (_value: string) => void = () => {};
  private onTouched = () => {};

  get containerClasses(): string {
    const baseClasses = `text-xs flex flex-row gap-2 items-center border-1 border-mbaza-gray-dark input-round input-height ${this.containerClass}`;
    return `${baseClasses}`;
  }

  get selectClasses(): string {
    return this.hasError ? 'error-select' : '';
  }

  get effectiveOptionOverflowSize(): number {
    return this.compactMobileDropdown && this.isMobileViewport ? 4 : 8;
  }

  get effectiveDropdownStyle(): Record<string, string> | null {
    return this.compactMobileDropdown && this.isMobileViewport
      ? { maxHeight: '40vh' }
      : null;
  }

  get mobileBackdropEnabled(): boolean {
    return this.compactMobileDropdown && this.isMobileViewport;
  }

  get useNativeMobileSelect(): boolean {
    return this.compactMobileDropdown && this.isMobileViewport;
  }

  private get isMobileViewport(): boolean {
    return (
      isPlatformBrowser(this.platformId) &&
      window.innerWidth < 768
    );
  }

  writeValue(value: string): void {
    this.value = value;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.controlDisabled = isDisabled;
  }

  onValueChange(value: string): void {
    this.value = value;
    this.onChange(value);
    this.onTouched();
    this.handleChange.emit(value);
  }
}
